import { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useNavSize } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { NakedButton, styles, Text } from '@frontend/design-system';
import { useTopBarShallowStore, useTopBarStore } from '../../store/use-top-bar-store';
import { navItemStyles, iconButtonStyles } from '../../styles/styles';
import { TopBarFocusIds } from '../../utils/focus-registry';

type LocationNavButtonProps = {
  isSelected: boolean;
  disabled: boolean;
  onClick: (e: any) => void;
  showLabel: boolean;
  label: React.ReactNode;
  isMobile: boolean;
};

export const LocationNavButton = memo(
  ({ isSelected, disabled, onClick, showLabel, label, isMobile }: LocationNavButtonProps) => (
    <NakedButton
      css={[
        iconButtonStyles({ isSelected, isMobile }),
        disabled &&
          css`
            pointer-events: none;
            :hover {
              background: inherit;
            }
          `,
      ]}
      onClick={onClick}
      id={TopBarFocusIds.location}
      data-test-id='nav-location-picker-button'
      trackingId='nav-location-picker-button'
    >
      {showLabel && label}
      <Icon name='location' />
    </NakedButton>
  )
);
LocationNavButton.displayName = 'LocationNavButton';

export const LocationNavButtonWrapper = () => {
  const user = getUser();
  const { t } = useTranslation();
  const navSize = useNavSize();
  const isXsmall = navSize.isLte('xsmall');
  const isMobile = navSize.isLte('mobile');

  const { handleSelectableIconClick: handleIconClick } = useTopBarShallowStore('handleSelectableIconClick');
  const isSelected = useTopBarStore((state) => state.selectedIcon === 'location');
  const { selectedLocationIds, accessibleLocationIds, accessibleGroupIds, getLocationName, accessibleGroupData } =
    useAppScopeStore();
  const applicableGroupIds = useMemo(
    () => accessibleGroupIds.filter((id) => !accessibleGroupData[id]?.children.some((child) => child.accessible)),
    [accessibleGroupIds, accessibleGroupData]
  );
  const singleLocationReferenceName = getLocationName(selectedLocationIds[0]);
  const shouldDisallowPicker = applicableGroupIds.concat(accessibleLocationIds).length === 1 && user?.type !== 'weave';

  const locationText = (
    <Text css={navItemStyles({ isXsmall, isMobile, isSelected, truncate: styles.truncate })}>
      {selectedLocationIds.length > 1
        ? `${selectedLocationIds.length} Locations`
        : singleLocationReferenceName || t('No location selected')}
    </Text>
  );

  return (
    <li>
      <LocationNavButton
        isSelected={isSelected}
        disabled={shouldDisallowPicker}
        onClick={(e: any) => {
          if (!shouldDisallowPicker) handleIconClick('location', e);
        }}
        showLabel={navSize.isGt('small')}
        label={locationText}
        isMobile={isMobile}
      />
    </li>
  );
};
